import { useCallback, useMemo, useState } from 'react';

import { LoadingIndicator } from '@bambi/component-library/src/components/LoadingIndicator/LoadingIndicator';
import { sortBy } from 'lodash-es';
import { DateTime } from 'luxon';
import { SubmitHandler, useFieldArray, useFormContext } from 'react-hook-form';
import { v4 as uuid } from 'uuid';

import { useAppDispatch, useAppSelector } from 'src/app/store/hooks';
import {
  OrderedMultiloadTripLocationDetailTemplate,
  RouteMultiloadTemplateRead,
  RouteTripTemplateRead,
} from 'src/common/external/bambi-api/routesApi';
import { show } from 'src/common/primitives/Toast/toast.slice';

import { RouteTemplateFormViewModeError } from './components/RouteTemplateFormViewModeError';
import { RouteTripTemplateList } from './components/RouteTripTemplateList';
import { RouteTemplateMultiloadTripForm } from './RouteTemplateMultiloadTripForm';
import { RouteTemplateTripMatcherForm } from './RouteTemplateTripMatcherForm';
import {
  MultiloadPickupDropoffTripTemplateOrderItem,
  RouteMultiloadTemplateFormValues,
  RouteTemplateFormValues,
  RouteTripTemplateFormValues,
} from './types';
import { useSubmitRouteTemplateForm } from './useSubmitRouteTemplateForm';
import { useTripTemplatesList } from './useTripTemplatesList';

interface RouteTemplateFormStep2Props {
  onCancel: () => void;
  onSubmit: () => void;
  setStep: (step: number) => void;
}
export function RouteTemplateFormStep2({
  onCancel,
  onSubmit,
  setStep,
}: RouteTemplateFormStep2Props) {
  const dispatch = useAppDispatch();
  const form = useFormContext<RouteTemplateFormValues>();
  const workflowMode = useAppSelector((state) => state.routeTemplates.workflow);
  const [editingTripMatcher, setEditingTripMatcher] =
    useState<RouteTripTemplateFormValues>();

  const [currentView, setCurrentView] = useState<
    'add_trip_templates' | 'trip_templates_list' | 'multiload_trips' | 'saving'
  >('trip_templates_list');

  const routeTemplateId = form.getValues('id');
  const isEditing = !!routeTemplateId;
  const assignments = useAppSelector(
    (state) => state.routeTemplates.bulkCreationAssignments
  );
  const canCreateMore = assignments.length > 1;

  const { fields, remove, replace } = useFieldArray({
    control: form.control,
    name: 'trip_templates',
    keyName: '__id',
  });

  // Store the initial trip template ids, intentionally not recomputed to
  // compare against the current trip template ids for deletion
  const tripTemplateIds = useMemo(() => {
    return form.getValues('trip_templates').map((f) => f.id ?? '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Store the initial multiload trip template ids, intentionally not recomputed
  // to compare against the current multiload trip template ids for deletion
  const multiloadTripTemplateIds = useMemo(() => {
    return form.getValues('multiload_trip_templates').map((f) => f.id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const multiloadTripTemplates = form.watch('multiload_trip_templates');
  // Using the hook to generate the list of trip templates that can be selected
  // for multi-load trips
  const { tripTemplates: selectableTripTemplates } = useTripTemplatesList(
    fields,
    multiloadTripTemplates
  );

  const [editingMultiLoadTripTemplate, setEditingMultiLoadTripTemplate] =
    useState<RouteMultiloadTemplateFormValues>();

  const handleSubmitTripMatcherForm = useCallback<
    SubmitHandler<RouteTripTemplateRead>
  >(
    (routeTemplateFields) => {
      const updatedFields = [...fields];
      if (!editingTripMatcher) {
        updatedFields.push({
          ...routeTemplateFields,
          __id: uuid(),
        });
      } else {
        const tripMatcherIndex = fields.findIndex(
          (f) => f.__id === (editingTripMatcher as typeof f).__id
        );
        updatedFields[tripMatcherIndex] = {
          ...fields[tripMatcherIndex],
          ...routeTemplateFields,
        };
      }

      const fs = sortBy(updatedFields, (f) =>
        DateTime.fromISO(f.scheduled_pickup_time).toMillis()
      );

      replace(fs);

      setEditingTripMatcher(undefined);
      setCurrentView('trip_templates_list');
    },
    [fields, editingTripMatcher, replace]
  );

  const handleSubmitMultiLoadTripsForm = useCallback(
    (
      orderedMultiLoadItems: MultiloadPickupDropoffTripTemplateOrderItem[],
      selectedTripTemplates: Array<
        RouteTripTemplateFormValues & { __id: string }
      >
    ) => {
      // Handle updating the existing multiload trip template
      if (editingMultiLoadTripTemplate?.configuration.id) {
        // Find the template in the form and update it
        const multiloadTripTemplateIndex = multiloadTripTemplates.findIndex(
          (template) =>
            template.id === editingMultiLoadTripTemplate.configuration.id
        );

        if (multiloadTripTemplateIndex !== -1) {
          form.setValue(
            `multiload_trip_templates.${multiloadTripTemplateIndex}.template_trip_location_details`,
            orderedMultiLoadItems.map((item, index) => {
              const trip_location_detail_id =
                item.type === 'pickup'
                  ? item.trip.pickup.id
                  : item.trip.dropoff.id;

              const result: OrderedMultiloadTripLocationDetailTemplate = {
                order: 1 + index,
                trip_location_detail_id,
              };

              return result;
            })
          );
        }

        setEditingMultiLoadTripTemplate(undefined);
        setCurrentView('trip_templates_list');
        return;
      }

      // Create multiload trip template within the form
      const newMultiloadTripTemplate = {
        template_trip_location_details: orderedMultiLoadItems.map(
          (item, index) => {
            const trip_location_detail_id =
              item.type === 'pickup'
                ? item.trip.pickup.id
                : item.trip.dropoff.id;

            const result: OrderedMultiloadTripLocationDetailTemplate = {
              order: 1 + index,
              trip_location_detail_id,
            };

            return result;
          }
        ),
        // Missing some DB fields like created_at, etc, but close enough for the form.
      } as RouteMultiloadTemplateRead;

      form.setValue(
        'multiload_trip_templates',
        [...multiloadTripTemplates, newMultiloadTripTemplate].filter(Boolean)
      );

      setEditingMultiLoadTripTemplate(undefined);
      setCurrentView('trip_templates_list');
    },
    [form, multiloadTripTemplates, editingMultiLoadTripTemplate]
  );

  const handleEditTripMatcher = useCallback(
    (matcher: RouteTripTemplateFormValues) => {
      setEditingTripMatcher(matcher);
      setCurrentView('add_trip_templates');
    },
    []
  );

  const handleDeleteTripMatcher = useCallback(
    (matcher: RouteTripTemplateFormValues & { __id: string }) => {
      const itemIndex = fields.findIndex((m) => m.__id === matcher.__id);
      if (itemIndex === -1) {
        dispatch(
          show({
            type: 'error',
            title: 'Failed to delete trip template',
            description: 'Trip template not found',
          })
        );
        return;
      }

      remove(itemIndex);
    },
    [dispatch, fields, remove]
  );

  const handleEditMultiLoadTripTemplate = useCallback(
    (multiloadTemplate: RouteMultiloadTemplateFormValues) => {
      setEditingMultiLoadTripTemplate(multiloadTemplate);
      setCurrentView('multiload_trips');
    },
    []
  );

  const handleRestoreMultiLoadTripTemplate = useCallback(
    (matcher: RouteMultiloadTemplateFormValues) => {
      const updatedMultiloadTripTemplates = multiloadTripTemplates.filter(
        (template) => template !== matcher.configuration
      );

      form.setValue('multiload_trip_templates', updatedMultiloadTripTemplates);
    },
    [form, multiloadTripTemplates]
  );

  const { saveAndClose, saveAndNext } = useSubmitRouteTemplateForm(
    isEditing,
    tripTemplateIds,
    multiloadTripTemplateIds,
    onSubmit,
    () => setCurrentView('trip_templates_list')
  );

  const handleSaveAndClose = useCallback<
    SubmitHandler<RouteTemplateFormValues>
  >(
    async (data) => {
      setCurrentView('saving');
      await saveAndClose(data);
    },
    [saveAndClose]
  );

  const handleSaveAndNext = useCallback<SubmitHandler<RouteTemplateFormValues>>(
    async (data) => {
      setCurrentView('saving');
      await saveAndNext(data);
      setStep(1);
    },
    [saveAndNext, setStep]
  );

  return (
    <>
      <div className="mt-4 flex flex-col gap-4">
        {currentView === 'add_trip_templates' ? (
          <RouteTemplateTripMatcherForm
            onCancel={() => setCurrentView('trip_templates_list')}
            onSubmit={handleSubmitTripMatcherForm}
            values={editingTripMatcher}
          />
        ) : currentView === 'trip_templates_list' ? (
          <RouteTripTemplateList
            tripTemplates={fields}
            multiloadTripTemplates={multiloadTripTemplates}
            onAddTripTemplate={() => setCurrentView('add_trip_templates')}
            onEditTripTemplate={handleEditTripMatcher}
            onDeleteTripTemplate={handleDeleteTripMatcher}
            workflowMode={workflowMode}
            canCreateMore={canCreateMore}
            onCancel={onCancel}
            onSaveAndClose={form.handleSubmit(handleSaveAndClose)}
            onSaveAndNext={form.handleSubmit(handleSaveAndNext)}
            onMultiLoadTrips={() => setCurrentView('multiload_trips')}
            onEditMultiLoadTripTemplate={handleEditMultiLoadTripTemplate}
            onRestoreMultiLoadTripTemplate={handleRestoreMultiLoadTripTemplate}
          />
        ) : currentView === 'multiload_trips' ? (
          <RouteTemplateMultiloadTripForm
            multiloadTripTemplate={editingMultiLoadTripTemplate}
            tripTemplates={
              // At this stage we are guaranteed to have the __id field, so assert the correct type
              (editingMultiLoadTripTemplate?.trip_templates as
                | (RouteTripTemplateFormValues & {
                    __id: string;
                  })[]
                | undefined) ?? selectableTripTemplates
            }
            skipSelect={!!editingMultiLoadTripTemplate}
            onCancel={() => setCurrentView('trip_templates_list')}
            onSubmit={handleSubmitMultiLoadTripsForm}
          />
        ) : // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
        currentView === 'saving' ? (
          <div className="flex flex-row justify-center">
            <LoadingIndicator />
          </div>
        ) : (
          // This shouldn't happen, but just in case
          <RouteTemplateFormViewModeError />
        )}
      </div>
    </>
  );
}
